<div class="training-schedule-container pb-20">
  <!-- Loading State -->
  @if (loading) {
    <div class="text-center py-10">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'common.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-3">{{ 'manageEvent.trainingSchedule.loading' | translate }}</p>
    </div>
  } @else {
    <!-- Header with Stats -->
    <div class="schedule-header mb-6">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h3 class="mb-1">
            <i class="fa fa-calendar-alt text-primary me-2"></i>
            {{ 'manageEvent.trainingSchedule.title' | translate }}
          </h3>
          <p class="text-muted mb-0">{{ 'manageEvent.trainingSchedule.subtitle' | translate }}</p>
        </div>
        <div class="d-flex gap-4">
          <div class="stat-badge stat-pending">
            <i class="fa fa-clock me-1"></i>
            <span>{{ pendingCount }} {{ 'manageEvent.trainingSchedule.pending' | translate }}</span>
          </div>
          <div class="stat-badge stat-complete">
            <i class="fa fa-check-circle me-1"></i>
            <span>{{ completeCount }} {{ 'manageEvent.trainingSchedule.complete' | translate }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Day Tabs -->
    <div class="day-tabs mb-4">
      @for (day of eventDays; track day.dateStr) {
        <button
          class="day-tab"
          [class.active]="selectedDay?.dateStr === day.dateStr"
          [class.today]="day.isToday"
          (click)="selectDay(day)"
        >
          <span class="day-label">{{ day.label }}</span>
          <span class="session-count">{{ getSessionCountForDay(day) }} {{ 'manageEvent.trainingSchedule.sessions' | translate }}</span>
        </button>
      }
    </div>

    <!-- Main Content: Two Panels -->
    <div class="schedule-panels">
      <!-- Left Panel: Participants -->
      <div class="panel participants-panel">
        <div class="panel-header">
          <h4 class="mb-0">
            <i class="fa fa-users me-2"></i>
            {{ 'manageEvent.trainingSchedule.participants' | translate }}
          </h4>
          <div class="filter-buttons">
            <button
              class="filter-btn"
              [class.active]="participantFilter === 'all'"
              (click)="participantFilter = 'all'"
            >{{ 'manageEvent.trainingSchedule.filterAll' | translate }}</button>
            <button
              class="filter-btn"
              [class.active]="participantFilter === 'pending'"
              (click)="participantFilter = 'pending'"
            >{{ 'manageEvent.trainingSchedule.filterPending' | translate }}</button>
            <button
              class="filter-btn"
              [class.active]="participantFilter === 'complete'"
              (click)="participantFilter = 'complete'"
            >{{ 'manageEvent.trainingSchedule.filterComplete' | translate }}</button>
          </div>
        </div>

        <div class="participants-list" cdkDropList [cdkDropListData]="filteredParticipants" [cdkDropListConnectedTo]="sessionDropLists" [cdkDropListSortingDisabled]="true">
          @if (filteredParticipants.length === 0) {
            <div class="empty-state">
              <i class="fa fa-user-slash fs-3 text-muted mb-2"></i>
              <p class="text-muted mb-0">{{ 'manageEvent.trainingSchedule.noParticipants' | translate }}</p>
            </div>
          } @else {
            @for (participant of filteredParticipants; track participant.registration.registration_id) {
                <div
                  class="participant-card"
                  [ngClass]="getParticipantStatusClass(participant)"
                  cdkDrag
                  [cdkDragData]="participant"
                  [cdkDragDisabled]="isParticipantCompleteForDay(participant)"
                >
                  <div class="participant-drag-handle">
                    <i class="fa fa-grip-vertical"></i>
                  </div>
                  <div class="participant-info">
                    <div class="participant-name">
                      <i class="fa" [ngClass]="getParticipantStatusIcon(participant)"></i>
                      {{ participant.userName }}
                    </div>
                    @if (participant.horseName) {
                      <div class="participant-horse">
                        <i class="fa fa-horse me-1"></i>
                        {{ participant.horseName }}
                      </div>
                    }
                    <div class="participant-package">
                      <span class="badge badge-light-primary">{{ participant.packageName }}</span>
                      <span class="session-type-badge" [ngClass]="'type-' + participant.sessionType">
                        {{ participant.sessionType === 'both' ? 'Any' : participant.sessionType }}
                      </span>
                    </div>
                  </div>
                  <div class="participant-progress">
                    @if (getDayProgress(participant); as dayProg) {
                      <div class="progress-text">
                        {{ dayProg.assigned }} / {{ dayProg.expected }}
                      </div>
                      <div class="progress-bar-container">
                        <div
                          class="progress-bar-fill"
                          [style.width.%]="dayProg.expected > 0 ? (dayProg.assigned / dayProg.expected) * 100 : 0"
                        ></div>
                      </div>
                    } @else {
                      <div class="progress-text">
                        {{ participant.assignedSessions }} / {{ participant.totalSessions }}
                      </div>
                      <div class="progress-bar-container">
                        <div
                          class="progress-bar-fill"
                          [style.width.%]="participant.totalSessions > 0 ? (participant.assignedSessions / participant.totalSessions) * 100 : 0"
                        ></div>
                      </div>
                    }
                  </div>

                  <!-- Drag Preview -->
                  <div *cdkDragPreview class="participant-drag-preview">
                    <i class="fa fa-user me-2"></i>
                    {{ participant.userName }}
                  </div>
                </div>
            }
          }
        </div>
      </div>

      <!-- Right Panel: Sessions Timeline -->
      <div class="panel sessions-panel">
        <div class="panel-header">
          <h4 class="mb-0">
            <i class="fa fa-list-timeline me-2"></i>
            {{ 'manageEvent.trainingSchedule.sessionsFor' | translate }} {{ selectedDay?.label }}
          </h4>
          <button class="btn btn-primary btn-sm" (click)="openSessionForm()">
            <i class="fa fa-plus me-1"></i>
            {{ 'manageEvent.trainingSchedule.addSession' | translate }}
          </button>
        </div>

        <div class="sessions-list">
          @if (currentDaySessions.length === 0) {
            <div class="empty-state">
              <i class="fa fa-calendar-xmark fs-3 text-muted mb-2"></i>
              <p class="text-muted mb-2">{{ 'manageEvent.trainingSchedule.noSessions' | translate }}</p>
              <button class="btn btn-outline-primary btn-sm" (click)="openSessionForm()">
                <i class="fa fa-plus me-1"></i>
                {{ 'manageEvent.trainingSchedule.createFirstSession' | translate }}
              </button>
            </div>
          } @else {
            @for (sessionView of currentDaySessions; track sessionView.session.session_id) {
              <div
                class="session-card"
                [class.session-break]="sessionView.session.session_type === 'break'"
                [class.session-startlist]="sessionView.session.session_type === 'startlist'"
                [class.session-full]="sessionView.isFull"
                cdkDropList
                #sessionDropList="cdkDropList"
                [cdkDropListData]="sessionView"
                [cdkDropListDisabled]="sessionView.session.session_type === 'startlist' || sessionView.session.session_type === 'break'"
                (cdkDropListDropped)="onDrop($event, sessionView)"
              >
                <div class="session-header">
                  <div class="session-time">
                    <span class="time-badge">
                      {{ formatTime(sessionView.session.start_time) }} - {{ formatTime(sessionView.session.end_time) }}
                    </span>
                    <span class="duration-badge">
                      {{ formatDuration(sessionView.session.start_time, sessionView.session.end_time) }}
                    </span>
                  </div>
                  <div class="session-actions">
                    @if (sessionView.session.session_type === 'startlist') {
                      <button
                        class="btn btn-icon btn-sm text-primary"
                        (click)="syncStartlist(sessionView)"
                        [disabled]="sessionView.syncing"
                        [title]="'manageEvent.trainingSchedule.syncWithPackages' | translate"
                      >
                        <i class="fa" [ngClass]="sessionView.syncing ? 'fa-spinner fa-spin' : 'fa-sync'"></i>
                      </button>
                    }
                    <button class="btn btn-icon btn-sm" (click)="openSessionForm(sessionView.session)" [title]="'common.edit' | translate">
                      <i class="fa fa-edit"></i>
                    </button>
                    <button class="btn btn-icon btn-sm text-danger" (click)="deleteSession(sessionView.session)" [title]="'common.delete' | translate">
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </div>

                <div class="session-info">
                  <div class="session-title">
                    <i class="fa" [ngClass]="getSessionTypeIcon(sessionView.session.session_type)"></i>
                    {{ sessionView.session.session_name }}
                  </div>
                  <div class="session-meta">
                    <span class="type-label">{{ getSessionTypeLabel(sessionView.session.session_type) }}</span>
                    @if (sessionView.session.instructor) {
                      <span class="instructor">
                        <i class="fa fa-chalkboard-teacher me-1"></i>
                        {{ sessionView.session.instructor }}
                      </span>
                    }
                    @if (sessionView.session.location) {
                      <span class="location">
                        <i class="fa fa-location-dot me-1"></i>
                        {{ sessionView.session.location }}
                      </span>
                    }
                  </div>
                </div>

                @if (sessionView.session.session_type !== 'break') {
                  <div class="session-capacity">
                    <span [class.text-danger]="sessionView.isFull">
                      {{ sessionView.participantCount }} / {{ sessionView.session.max_participants || '∞' }}
                    </span>
                    {{ 'manageEvent.trainingSchedule.participantsLabel' | translate }}
                  </div>

                  <!-- Drop Zone / Assigned Participants -->
                  <div class="session-assignments">
                    @if (sessionView.session.session_type === 'startlist') {
                      <!-- Startlist view - shows times, no editing -->
                      @if (sessionView.assignments.length === 0) {
                        <div class="text-center text-muted py-3">
                          <i class="fa fa-list-ol me-2"></i>
                          {{ 'manageEvent.trainingSchedule.noParticipantsStartlist' | translate }}
                        </div>
                      } @else {
                        @for (assignment of sessionView.assignments; track assignment.assignment_id; let i = $index) {
                          <div class="assigned-participant startlist-entry">
                            <div class="startlist-number">{{ i + 1 }}</div>
                            <div class="startlist-time">
                              {{ getParticipantStartTime(sessionView.session, i) }}
                            </div>
                            <div class="assigned-info flex-grow-1">
                              <span class="assigned-name">{{ getAssignmentUserName(assignment) }}</span>
                              @if (getAssignmentHorseName(assignment)) {
                                <span class="assigned-horse ms-1">
                                  ({{ getAssignmentHorseName(assignment) }})
                                </span>
                              }
                            </div>
                          </div>
                        }
                      }
                    } @else {
                      <!-- Regular session view - editable -->
                      @if (sessionView.assignments.length === 0) {
                        <div class="drop-zone">
                          <i class="fa fa-arrow-down-to-line"></i>
                          <span>{{ 'manageEvent.trainingSchedule.dropParticipantsHere' | translate }}</span>
                        </div>
                      } @else {
                        @for (assignment of sessionView.assignments; track assignment.assignment_id) {
                          <div class="assigned-participant">
                            <div class="assigned-info">
                              <i class="fa fa-user me-1"></i>
                              <span class="assigned-name">{{ getAssignmentUserName(assignment) }}</span>
                              @if (getAssignmentHorseName(assignment)) {
                                <span class="assigned-horse">
                                  ({{ getAssignmentHorseName(assignment) }})
                                </span>
                              }
                            </div>
                            <button
                              class="btn btn-icon btn-xs text-danger"
                              (click)="removeAssignment(assignment, sessionView)"
                              [title]="'common.remove' | translate"
                            >
                              <i class="fa fa-times"></i>
                            </button>
                          </div>
                        }
                        @if (!sessionView.isFull) {
                          <div class="drop-zone drop-zone-small">
                            <i class="fa fa-plus"></i>
                            <span>{{ 'manageEvent.trainingSchedule.addMore' | translate }}</span>
                          </div>
                        }
                      }
                    }
                  </div>
                }
              </div>
            }
          }
        </div>
      </div>
    </div>
  }

  <!-- Session Form Modal -->
  @if (showSessionForm) {
    <div class="modal-backdrop" (click)="closeSessionForm()"></div>
    <div class="session-form-modal">
      <div class="modal-header">
        <h5 class="modal-title">
          {{ editingSession ? ('manageEvent.trainingSchedule.editSession' | translate) : ('manageEvent.trainingSchedule.createSession' | translate) }}
        </h5>
        <button class="btn-close" (click)="closeSessionForm()"></button>
      </div>
      <div class="modal-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label">{{ 'manageEvent.trainingSchedule.sessionName' | translate }} *</label>
            <input
              type="text"
              class="form-control"
              [(ngModel)]="sessionForm.session_name"
              [placeholder]="'manageEvent.trainingSchedule.sessionNamePlaceholder' | translate"
            />
          </div>

          <div class="col-12">
            <label class="form-label">{{ 'manageEvent.trainingSchedule.description' | translate }}</label>
            <textarea
              class="form-control"
              [(ngModel)]="sessionForm.session_description"
              rows="2"
              [placeholder]="'manageEvent.trainingSchedule.descriptionPlaceholder' | translate"
            ></textarea>
          </div>

          <div class="col-md-6">
            <label class="form-label">{{ 'manageEvent.trainingSchedule.sessionType' | translate }} *</label>
            <select class="form-select" [(ngModel)]="sessionForm.session_type">
              <option value="individual">{{ 'manageEvent.trainingSchedule.typeIndividual' | translate }}</option>
              <option value="group">{{ 'manageEvent.trainingSchedule.typeGroup' | translate }}</option>
              <option value="startlist">{{ 'manageEvent.trainingSchedule.typeStartlist' | translate }}</option>
              <option value="break">{{ 'manageEvent.trainingSchedule.typeBreak' | translate }}</option>
            </select>
          </div>

          <div class="col-md-6">
            <label class="form-label">{{ 'manageEvent.trainingSchedule.maxParticipants' | translate }}</label>
            <input
              type="number"
              class="form-control"
              [(ngModel)]="sessionForm.max_participants"
              min="1"
              [disabled]="sessionForm.session_type === 'break' || sessionForm.session_type === 'startlist'"
            />
          </div>

          @if (sessionForm.session_type === 'startlist') {
            <div class="col-md-6">
              <label class="form-label">{{ 'manageEvent.trainingSchedule.package' | translate }} *</label>
              <select class="form-select" [(ngModel)]="sessionForm.package_id">
                <option [ngValue]="null">{{ 'manageEvent.trainingSchedule.selectPackage' | translate }}</option>
                @for (pkg of packages; track pkg.package_id) {
                  <option [ngValue]="pkg.package_id">{{ pkg.package_name }}</option>
                }
              </select>
              <small class="text-muted">{{ 'manageEvent.trainingSchedule.packageHint' | translate }}</small>
            </div>

            <div class="col-md-6">
              <label class="form-label">{{ 'manageEvent.trainingSchedule.timePerParticipant' | translate }}</label>
              <input
                type="number"
                class="form-control"
                [(ngModel)]="sessionForm.time_per_participant"
                min="1"
                placeholder="5"
              />
              <small class="text-muted">{{ 'manageEvent.trainingSchedule.timePerParticipantHint' | translate }}</small>
            </div>
          }

          <div class="col-md-6">
            <label class="form-label">{{ 'manageEvent.trainingSchedule.startTime' | translate }} *</label>
            <input
              type="time"
              class="form-control"
              [(ngModel)]="sessionForm.start_time"
            />
          </div>

          <div class="col-md-6">
            <label class="form-label">{{ 'manageEvent.trainingSchedule.endTime' | translate }} *</label>
            <input
              type="time"
              class="form-control"
              [(ngModel)]="sessionForm.end_time"
            />
          </div>

          <div class="col-md-6">
            <label class="form-label">{{ 'manageEvent.trainingSchedule.instructor' | translate }}</label>
            <input
              type="text"
              class="form-control"
              [(ngModel)]="sessionForm.instructor"
              [placeholder]="'manageEvent.trainingSchedule.instructorPlaceholder' | translate"
            />
          </div>

          <div class="col-md-6">
            <label class="form-label">{{ 'manageEvent.trainingSchedule.location' | translate }}</label>
            <input
              type="text"
              class="form-control"
              [(ngModel)]="sessionForm.location"
              [placeholder]="'manageEvent.trainingSchedule.locationPlaceholder' | translate"
            />
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-light" (click)="closeSessionForm()">{{ 'common.cancel' | translate }}</button>
        <button
          class="btn btn-primary"
          (click)="saveSession()"
          [disabled]="!sessionForm.session_name || !sessionForm.start_time || !sessionForm.end_time || (sessionForm.session_type === 'startlist' && !sessionForm.package_id)"
        >
          {{ editingSession ? ('manageEvent.trainingSchedule.saveChanges' | translate) : ('manageEvent.trainingSchedule.createSession' | translate) }}
        </button>
      </div>
    </div>
  }
</div>
